import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { ChevronLeft, ChevronRight, X, Volume1, Sun, Headphones } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const src = (u) => (u.startsWith('/api/') ? `${API}${u}` : u);
export const SOUND = { quiet: 'Quiet', some: 'Some noise', loud: 'Loud moments' };
export const LIGHT = { soft: 'Soft', dimmable: 'Dimmable', bright: 'Bright', changing: 'Changing' };

const SensoryNote = ({ p }) => (p.sound || p.light || p.sensory_note ? (
  <div className="ac-sensory" data-testid="gallery-sensory-note">
    <div className="ac-sensory-levels">
      {p.sound && <span className={`ac-sensory-chip is-${p.sound}`} data-testid="gallery-sensory-sound"><Volume1 size={15} aria-hidden="true" /> Sound: {SOUND[p.sound]}</span>}
      {p.light && <span className="ac-sensory-chip" data-testid="gallery-sensory-light"><Sun size={15} aria-hidden="true" /> Light: {LIGHT[p.light]}</span>}
    </div>
    {p.sensory_note && <p data-testid="gallery-sensory-text">{p.sensory_note}</p>}
    {p.sound === 'loud' && <p className="ac-ear-tip" data-testid="gallery-ear-defender-tip"><Headphones size={15} aria-hidden="true" /> Gentle reminder: you&rsquo;re welcome to bring ear defenders or headphones for the louder moments.</p>}
    <small>Planned settings. We confirm them before sessions open.</small>
  </div>
) : null);

const TITLES = { flight: 'Inside the flight simulator', rail: 'Inside the train cab' };

const Lightbox = ({ photos, index, setIndex, pathway }) => {
  const touch = useRef(null);
  const closeRef = useRef(null);
  const n = photos.length;
  const go = (d) => setIndex((i) => (i + d + n) % n);
  useEffect(() => {
    closeRef.current?.focus();
    const onKey = (e) => {
      if (e.key === 'Escape') setIndex(null);
      if (e.key === 'ArrowRight') setIndex((i) => (i + 1) % n);
      if (e.key === 'ArrowLeft') setIndex((i) => (i - 1 + n) % n);
    };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => { document.removeEventListener('keydown', onKey); document.body.style.overflow = ''; };
  }, [n, setIndex]);
  const p = photos[index];
  return (
    <div className="ac-lightbox" role="dialog" aria-modal="true" aria-label={`${TITLES[pathway]} photo ${index + 1} of ${n}`} data-testid={`gallery-lightbox-${pathway}`}
      onClick={(e) => e.target === e.currentTarget && setIndex(null)}
      onTouchStart={(e) => { touch.current = e.touches[0].clientX; }}
      onTouchEnd={(e) => { const dx = e.changedTouches[0].clientX - (touch.current ?? 0); if (Math.abs(dx) > 40) go(dx < 0 ? 1 : -1); }}>
      <button ref={closeRef} className="ac-lightbox-close" onClick={() => setIndex(null)} aria-label="Close" data-testid="gallery-lightbox-close"><X size={22} /></button>
      <button className="ac-lightbox-nav is-prev" onClick={() => go(-1)} aria-label="Previous photo" data-testid="gallery-lightbox-prev"><ChevronLeft size={26} /></button>
      <figure className="ac-lightbox-figure">
        <img src={src(p.image)} alt={p.alt} data-testid="gallery-lightbox-image" />
        <figcaption><span data-testid="gallery-lightbox-caption">{p.caption}</span><span className="ac-lightbox-count" data-testid="gallery-lightbox-count">{index + 1} / {n}</span></figcaption>
        <SensoryNote p={p} />
      </figure>
      <button className="ac-lightbox-nav is-next" onClick={() => go(1)} aria-label="Next photo" data-testid="gallery-lightbox-next"><ChevronRight size={26} /></button>
    </div>
  );
};

export const SimulatorGallery = ({ pathway, compact = false }) => {
  const [photos, setPhotos] = useState([]);
  const [index, setIndex] = useState(null);
  useEffect(() => { axios.get(`${API}/api/academy/galleries/${pathway}`).then((r) => setPhotos(r.data.photos)).catch(() => setPhotos([])); }, [pathway]);
  if (!photos.length) return null;
  const H = compact ? 'h3' : 'h2';
  return (
    <section className={`ac-gallery${compact ? ' is-compact' : ''}`} aria-label={TITLES[pathway]} data-testid={`simulator-gallery-${pathway}`}>
      <H className="ac-gallery-title">{TITLES[pathway]}</H>
      {!compact && <p className="ac-gallery-lede">A first look at the space, so your child knows what to expect. Tap a photo to see it larger.</p>}
      <ul className="ac-gallery-grid">
        {photos.map((p, i) => (
          <li key={p.image}>
            <button type="button" className="ac-gallery-item" onClick={() => setIndex(i)} aria-label={`Open photo: ${p.caption || p.alt}`} data-testid={`gallery-photo-${pathway}-${i}`}>
              <img src={src(p.image)} alt={p.alt} loading="lazy" width="1264" height="848" />
              {p.caption && <span className="ac-gallery-caption">{p.caption}</span>}
            </button>
          </li>
        ))}
      </ul>
      {index !== null && <Lightbox photos={photos} index={index} setIndex={setIndex} pathway={pathway} />}
    </section>
  );
};

export default SimulatorGallery;
